import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../../../../Store/Store';

interface Position {
  latitude: number;
  longitude: number;
}

interface GeoVerifyScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const containerStyle = {
  margin: '0 auto',
  padding: 'clamp(8px, 2vw, 20px)',
  width: '100%',
  maxWidth: '1100px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
};

const buttonStyle = {
  padding: '12px 24px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold' as const,
  minHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const errorContainerStyle = {
  padding: '10px 40px 10px 10px',
  margin: '10px 0',
  border: '1px solid #ff4444',
  borderRadius: '4px',
  backgroundColor: '#ffe6e6',
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const closeButtonStyle = {
  position: 'absolute' as const,
  top: '5px',
  right: '10px',
  background: 'none',
  border: 'none',
  fontSize: '18px',
  fontWeight: 'bold' as const,
  cursor: 'pointer',
  color: '#ff4444',
  padding: '0',
  lineHeight: '1',
  width: '20px',
  height: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50dvh',
  width: '100%',
  fontSize: '1.2rem',
};

// Error popup styles
const errorPopupOverlayStyle = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2000,
};

const errorPopupStyle = {
  backgroundColor: '#ffe6e6',
  border: '2px solid #ff4444',
  borderRadius: '8px',
  padding: '20px',
  maxWidth: '400px',
  width: '90%',
  textAlign: 'center' as const,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  position: 'relative' as const,
};

const errorPopupButtonStyle = {
  backgroundColor: '#ff4444',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '10px 20px',
  cursor: 'pointer',
  marginTop: '15px',
  fontSize: '14px',
  fontWeight: 'bold' as const,
};

const GeoVerifyScreen: React.FC<GeoVerifyScreenProps> = ({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}) => {
  const { product, backendBaseUrl, setGlobalNotification, style } =
    useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const hasVerifiedRef = useRef(false); // Use ref instead of state to persist across re-renders
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const geocoderRef = useRef<any>(null);

  // Request user location on component mount
  useEffect(() => {
    // Skip if already verified in this session
    if (sessionStorage.getItem(`geo_verified_${product?.code}`)) {
      const savedResult = sessionStorage.getItem(`geo_result_${product?.code}`);
      const savedPosition = sessionStorage.getItem(
        `geo_position_${product?.code}`
      );

      if (savedResult && savedPosition) {
        // savedResult is a plain string ('success' or 'failure'), not JSON
        const resultData = savedResult;
        // Parse position from "latitude,longitude" string format
        const [lat, lng] = savedPosition.split(',').map(Number);
        const positionData = { latitude: lat, longitude: lng };
        setVerificationResult(resultData);
        setPosition(positionData);
        hasVerifiedRef.current = true;
        return;
      }
    }

    // Initialize geocoder
    if ((window as any).google?.maps?.Geocoder) {
      geocoderRef.current = new (window as any).google.maps.Geocoder();
    }

    requestUserLocation();
  }, [product]);

  // Initialize map when position is available and verification is successful
  useEffect(() => {
    if (
      position &&
      verificationResult === 'success' &&
      product?.geo_attributes?.position &&
      mapRef.current
    ) {
      const [geoLat, geoLon] = product.geo_attributes.position
        .split(',')
        .map(Number);
      initializeMap({ latitude: geoLat, longitude: geoLon });
    }
  }, [position, verificationResult, product?.geo_attributes?.position]);

  const requestUserLocation = async () => {
    if (!navigator.geolocation) {
      showError('Geolocation is not supported by this browser');
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    try {
      console.log('Requesting user location for verification...');
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        }
      );

      const newPosition = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      console.log('Position revealed for verification:', newPosition);
      setPosition(newPosition);
      await verifyGeoPosition(newPosition);
    } catch (error) {
      console.error('Error getting location:', error);
      showError(
        'Position is required for verification. Please allow location access.'
      );
      setTimeout(() => requestUserLocation(), 2000);
    }
  };

  const initializeMap = (center: Position) => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: center.latitude, lng: center.longitude },
      zoom: 15,
    });

    // Add marker for geo position
    if (product?.geo_attributes?.position) {
      const geoCoords = product.geo_attributes.position.split(',');
      const geoLat = parseFloat(geoCoords[0]);
      const geoLng = parseFloat(geoCoords[1]);

      new window.google.maps.Marker({
        position: { lat: geoLat, lng: geoLng },
        map: map,
        title: 'Geo Position',
      });
    }
  };

  // Calculate distance between two points using Haversine formula (returns meters)
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in meters
    return distance;
  };

  // Get formatted address from coordinates
  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    if (!geocoderRef.current) return '';

    try {
      const result = await new Promise<any>((resolve, reject) => {
        geocoderRef.current.geocode(
          { location: { lat, lng } },
          (results: any, status: any) => {
            if (status === 'OK' && results && results.length > 0) {
              // Filter out Plus Codes and find a proper street address
              const properAddress = results.find((result: any) => {
                const address = result.formatted_address;
                // Check if it's not a Plus Code (contains +) and has street number/name
                return (
                  !address.includes('+') &&
                  (result.types.includes('street_address') ||
                    result.types.includes('premise') ||
                    result.types.includes('subpremise'))
                );
              });

              if (properAddress) {
                resolve(properAddress);
              } else {
                // If no proper street address, try to find one with route info
                const routeAddress = results.find(
                  (result: any) =>
                    !result.formatted_address.includes('+') &&
                    result.types.includes('route')
                );
                resolve(routeAddress || results[0]);
              }
            } else {
              reject(new Error('Geocoding failed'));
            }
          }
        );
      });

      // Additional filtering to clean up the address
      let address = result.formatted_address;

      // Remove Plus Codes if they somehow made it through
      if (address.includes('+')) {
        // Try to extract just the city and country part
        const parts = address.split(',').map((part: string) => part.trim());
        const cleanParts = parts.filter((part: string) => !part.includes('+'));
        address = cleanParts.length > 0 ? cleanParts.join(', ') : '';
      }

      return address;
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return '';
    }
  };

  const verifyGeoPosition = async (userPosition: Position) => {
    if (!product?.geo_attributes?.position) {
      showError('Geo position not found. Please set geo position first.');
      setVerificationResult('failure');
      hasVerifiedRef.current = true;
      // Store in session storage
      sessionStorage.setItem(`geo_verified_${product?.code}`, 'true');
      sessionStorage.setItem(`geo_result_${product?.code}`, 'failure');
      sessionStorage.setItem(
        `geo_position_${product?.code}`,
        `${userPosition.latitude},${userPosition.longitude}`
      );
      await logAccessAttempt('failure', userPosition);
      return;
    }

    // Prevent duplicate verification
    if (hasVerifiedRef.current) {
      console.log('Verification already attempted, skipping...');
      return;
    }

    // Check session storage as additional safeguard
    const sessionKey = `geo_verified_${product?.code}`;
    if (sessionStorage.getItem(sessionKey)) {
      console.log(
        'Verification already attempted in this session, skipping...'
      );
      return;
    }

    setLoading(true);
    hasVerifiedRef.current = true; // Set flag to prevent duplicate calls
    // Mark as verified in session storage immediately
    sessionStorage.setItem(sessionKey, 'true');

    try {
      const geoCoords = product.geo_attributes.position.split(',');
      const geoLat = parseFloat(geoCoords[0]);
      const geoLng = parseFloat(geoCoords[1]);

      // Calculate distance using Haversine formula
      const distance = calculateDistance(
        userPosition.latitude,
        userPosition.longitude,
        geoLat,
        geoLng
      );

      console.log('Distance to geo:', distance, 'meters');

      const isWithinRange =
        distance <= (style.geoAttributes.geo_verify_range || 50);

      if (isWithinRange) {
        setVerificationResult('success');
        sessionStorage.setItem(`geo_result_${product?.code}`, 'success');
        sessionStorage.setItem(
          `geo_position_${product?.code}`,
          `${userPosition.latitude},${userPosition.longitude}`
        );
        await logAccessAttempt('success', userPosition);
      } else {
        setError(
          `You are ${Math.round(distance)}m away from the geo. You need to be within ${style.geoAttributes.geo_verify_range || 50}m.`
        );
        setVerificationResult('failure');
        sessionStorage.setItem(`geo_result_${product?.code}`, 'failure');
        sessionStorage.setItem(
          `geo_position_${product?.code}`,
          `${userPosition.latitude},${userPosition.longitude}`
        );
        await logAccessAttempt('failure', userPosition);
      }
    } catch (error) {
      console.error('Verification failed:', error);
      setError('Verification failed. Please try again.');
      setVerificationResult('failure');
      sessionStorage.setItem(`geo_result_${product?.code}`, 'failure');
      sessionStorage.setItem(
        `geo_position_${product?.code}`,
        `${userPosition.latitude},${userPosition.longitude}`
      );
      await logAccessAttempt('failure', userPosition);
    } finally {
      setLoading(false);
    }
  };

  const logAccessAttempt = async (
    result: 'success' | 'failure',
    verificationPosition: Position
  ) => {
    try {
      const userAgent = navigator.userAgent;

      // Enhanced device detection
      const isMobile =
        /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const isTablet = /iPad|Android(?=.*Mobile)/i.test(userAgent);
      const isDesktop = !isMobile && !isTablet;

      let device = 'Unknown';
      if (isDesktop) device = 'Desktop';
      else if (isTablet) device = 'Tablet';
      else if (isMobile) device = 'Mobile';

      // Enhanced OS detection
      let os = 'Unknown';
      if (/Windows NT/i.test(userAgent)) os = 'Windows';
      else if (/Mac OS X/i.test(userAgent)) os = 'macOS';
      else if (/Linux/i.test(userAgent)) os = 'Linux';
      else if (/Android/i.test(userAgent)) os = 'Android';
      else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';

      // Enhanced browser detection
      let browser = 'Unknown';
      if (/Edg/i.test(userAgent)) browser = 'Edge';
      else if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent))
        browser = 'Chrome';
      else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
      else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent))
        browser = 'Safari';
      else if (/Opera|OPR/i.test(userAgent)) browser = 'Opera';

      // Create a device fingerprint for better identification
      const screenResolution = `${screen.width}x${screen.height}`;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const language = navigator.language;
      const platform = navigator.platform;

      // Simple device fingerprint (combine multiple factors)
      const deviceFingerprint = btoa(
        `${userAgent}-${screenResolution}-${timezone}-${language}-${platform}`
      ).substring(0, 32);

      // Get address from coordinates
      const address = await getAddressFromCoordinates(
        verificationPosition.latitude,
        verificationPosition.longitude
      );

      await axios.post(`${backendBaseUrl}/log-access-attempt/`, {
        product_code: product.code,
        device,
        os,
        browser,
        ip_address: '', // Could be obtained from a service
        position: `${verificationPosition.latitude},${verificationPosition.longitude}`,
        address: address, // Add address field
        result,
        // Additional device identification fields
        device_fingerprint: deviceFingerprint,
        screen_resolution: screenResolution,
        timezone: timezone,
        language: language,
        platform: platform,
        user_agent: userAgent,
      });
    } catch (error) {
      console.error('Failed to log access attempt:', error);
    }
  };

  const retryLocationDetection = () => {
    setError('');
    setShowErrorPopup(false);
    setVerificationResult(null);
    setPosition(null);
    hasVerifiedRef.current = false; // Reset verification flag to allow retry
    // Clear session storage to allow retry
    sessionStorage.removeItem(`geo_verified_${product?.code}`);
    sessionStorage.removeItem(`geo_result_${product?.code}`);
    sessionStorage.removeItem(`geo_position_${product?.code}`);
    requestUserLocation();
  };

  const handleLeaveFeedback = () => {
    console.log('handleLeaveFeedback called');
    onNext();
  };

  const handleDigitalContent = () => {
    console.log('handleDigitalContent called');
    const urlParams = new URLSearchParams(window.location.search);
    const productCode = urlParams.get('product');
    if (productCode) {
      // Extract the base product code without any /digital-content-pdf suffix
      const baseProductCode = productCode.split('/')[0];
      const url = `${backendBaseUrl}/digital-content/${baseProductCode}/`;
      fetch(url)
        .then(async res => {
          const data = await res.json().catch(() => null);
          if (!res.ok) throw new Error('Failed');
          if (data?.redirect_url) {
            window.open(data.redirect_url, '_blank');
            return;
          }
          if (data?.pdf_url) {
            window.open(data.pdf_url, '_blank');
            return;
          }
          if (data?.image_url) {
            window.open(data.image_url, '_blank');
            return;
          }
          // If server streamed a PDF directly (no JSON), open endpoint in a new tab
          window.open(url, '_blank');
        })
        .catch(() => {
          window.open(url, '_blank');
        });
    }
  };

  const handleButtonAction = (
    action: string,
    redirectUrl?: string,
    email?: string
  ) => {
    switch (action) {
      case 'email':
        if (email) {
          const subject = encodeURIComponent('Geo Verification Issue');
          const body = encodeURIComponent(
            `Product: ${product?.name}\nSerial Number: ${product?.serial_number || 'N/A'}\n\nPlease describe your issue:`
          );
          window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
        }
        break;
      case 'retry':
        retryLocationDetection();
        break;
      case 'feedback':
        handleLeaveFeedback();
        break;
      case 'digital_content':
        handleDigitalContent();
        break;
      case 'redirect':
        if (redirectUrl) {
          window.open(redirectUrl, '_blank');
        }
        break;
      default:
        console.warn('Unknown button action:', action);
    }
  };

  const renderButton = (
    buttonConfig: {
      bg_color?: string;
      text_color?: string;
      shadow_color?: string;
      indication?: string;
      action?: string;
      redirect?: string;
      email?: string;
    },
    defaultText: string,
    defaultAction: string = 'feedback'
  ) => {
    // Don't render button if no indication text is provided
    if (!buttonConfig.indication && !defaultText) {
      return null;
    }

    const action = buttonConfig.action || defaultAction;
    const buttonText = buttonConfig.indication || defaultText;

    return (
      <button
        onClick={() =>
          handleButtonAction(action, buttonConfig.redirect, buttonConfig.email)
        }
        style={{
          ...buttonStyle,
          backgroundColor: buttonConfig.bg_color || '#07296A',
          color: buttonConfig.text_color || 'white',
          padding: responsiveStyles.padding,
          fontSize: responsiveStyles.fontSize,
          flex: '1 1 140px',
          minWidth: '0',
          maxWidth: '200px',
          boxShadow:
            '0 5px 5px ' + (buttonConfig.shadow_color || 'transparent'),
        }}
        dangerouslySetInnerHTML={{
          __html: buttonText,
        }}
      />
    );
  };

  // Calculate responsive button styles based on viewport height
  const getResponsiveButtonStyles = () => {
    const viewportHeight = window.innerHeight;
    const maxAllowedHeight = viewportHeight * 0.33; // 33vh

    // Estimate button container height (buttons + gaps + margins)
    // Base: 2 buttons (~48px each) + gap (15px) + margins (40px) + padding = ~151px
    const estimatedHeight = 151;

    if (estimatedHeight > maxAllowedHeight) {
      // Reduce styles when container would exceed 33vh
      const scaleFactor = Math.max(0.6, maxAllowedHeight / estimatedHeight);
      return {
        padding: `${Math.max(6, 12 * scaleFactor)}px ${Math.max(12, 24 * scaleFactor)}px`,
        fontSize: `${Math.max(10, 14 * scaleFactor)}px`,
        gap: `${Math.max(8, 15 * scaleFactor)}px`,
        marginTop: `${Math.max(10, 20 * scaleFactor)}px`,
        marginBottom: `${Math.max(10, 20 * scaleFactor)}px`,
      };
    }

    // Default styles when there's enough space
    return {
      padding: '12px 24px',
      fontSize: '14px',
      gap: '15px',
      marginTop: '20px',
      marginBottom: '20px',
    };
  };

  const responsiveStyles = getResponsiveButtonStyles();

  // Function to show error popup
  const showError = (message: string) => {
    setError(message);
    setShowErrorPopup(true);
  };

  // Function to close error popup
  const closeErrorPopup = () => {
    setShowErrorPopup(false);
    setError('');
  };

  if (loading || !position) {
    return (
      <>
        <div style={containerStyle}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '20px',
            }}
          >
            <h3>Geo Verification</h3>
            <p>Detecting your location for geo verification...</p>
          </div>
        </div>

        {/* Error Popup */}
        {showErrorPopup && (
          <div
            style={errorPopupOverlayStyle}
            onClick={e => e.target === e.currentTarget && closeErrorPopup()}
          >
            <div style={errorPopupStyle}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                  color: '#ff4444',
                }}
              >
                Error
              </div>
              <div style={{ marginBottom: '15px', color: '#333' }}>{error}</div>
              <button onClick={closeErrorPopup} style={errorPopupButtonStyle}>
                OK
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div style={containerStyle}>
        <h1
          style={{
            padding: '10px',
            backgroundColor: 'transparent',
            textAlign: 'center',
            width: 'fit-content',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {verificationResult === 'success' ? (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  style.geoAttributes.geo_verify_success_indication ||
                  '<span style="color: #28a745; fontSize: 1.2em;">✓</span> Valvola originale',
              }}
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  style.geoAttributes.geo_verify_failure_indication ||
                  '<span style="color: #dc3545; fontSize: 1.2em;">✗</span> Valvola non originale',
              }}
            />
          )}
        </h1>
        <br />

        {verificationResult === 'success' && (
          <div
            style={{
              paddingBottom: responsiveStyles.marginBottom,
              marginBottom: responsiveStyles.marginBottom,
            }}
          >
            {style.geoAttributes.geo_verify_success_show_installation_date &&
              product?.geo_attributes?.installation_date && (
                <div
                  style={{
                    marginBottom: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                >
                  Data di installazione:{' '}
                  {new Date(
                    product.geo_attributes.installation_date
                  ).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })}
                </div>
              )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: responsiveStyles.gap,
                justifyContent: 'center',
                alignItems: 'stretch',
                marginTop: responsiveStyles.marginTop,
                boxSizing: 'border-box',
              }}
            >
              {renderButton(
                {
                  bg_color:
                    style.geoAttributes
                      .geo_verify_success_first_button_bg_color,
                  text_color:
                    style.geoAttributes
                      .geo_verify_success_first_button_text_color,
                  shadow_color:
                    style.geoAttributes
                      .geo_verify_success_first_button_shadow_color,
                  indication:
                    style.geoAttributes
                      .geo_verify_success_first_button_indication,
                  action:
                    style.geoAttributes.geo_verify_success_first_button_action,
                  redirect:
                    style.geoAttributes
                      .geo_verify_success_first_button_redirect,
                  email:
                    style.geoAttributes.geo_verify_success_first_button_email,
                },
                'Consulta certificato di garanzia',
                'digital_content'
              )}

              {renderButton(
                {
                  bg_color:
                    style.geoAttributes
                      .geo_verify_success_second_button_bg_color,
                  text_color:
                    style.geoAttributes
                      .geo_verify_success_second_button_text_color,
                  shadow_color:
                    style.geoAttributes
                      .geo_verify_success_second_button_shadow_color,
                  indication:
                    style.geoAttributes
                      .geo_verify_success_second_button_indication,
                  action:
                    style.geoAttributes.geo_verify_success_second_button_action,
                  redirect:
                    style.geoAttributes
                      .geo_verify_success_second_button_redirect,
                  email:
                    style.geoAttributes.geo_verify_success_second_button_email,
                },
                'Lascia un feedback',
                'feedback'
              )}
            </div>
          </div>
        )}

        {verificationResult === 'failure' && (
          <div
            style={{
              paddingBottom: responsiveStyles.marginBottom,
              marginBottom: responsiveStyles.marginBottom,
            }}
          >
            {style.geoAttributes.geo_verify_failure_show_installation_date &&
              product?.geo_attributes?.installation_date && (
                <div
                  style={{
                    marginBottom: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                >
                  Data di installazione:{' '}
                  {new Date(
                    product.geo_attributes.installation_date
                  ).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })}
                </div>
              )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap:
                  Math.max(
                    8,
                    parseInt(responsiveStyles.gap.replace('px', '')) * 0.67
                  ) + 'px',
                justifyContent: 'center',
                alignItems: 'stretch',
                boxSizing: 'border-box',
              }}
            >
              {renderButton(
                {
                  bg_color:
                    style.geoAttributes
                      .geo_verify_failure_first_button_bg_color,
                  text_color:
                    style.geoAttributes
                      .geo_verify_failure_first_button_text_color,
                  shadow_color:
                    style.geoAttributes
                      .geo_verify_failure_first_button_shadow_color,
                  indication:
                    style.geoAttributes
                      .geo_verify_failure_first_button_indication,
                  action:
                    style.geoAttributes.geo_verify_failure_first_button_action,
                  redirect:
                    style.geoAttributes
                      .geo_verify_failure_first_button_redirect,
                  email:
                    style.geoAttributes.geo_verify_failure_first_button_email,
                },
                'Retry Location',
                'retry'
              )}

              {renderButton(
                {
                  bg_color:
                    style.geoAttributes
                      .geo_verify_failure_second_button_bg_color,
                  text_color:
                    style.geoAttributes
                      .geo_verify_failure_second_button_text_color,
                  shadow_color:
                    style.geoAttributes
                      .geo_verify_failure_second_button_shadow_color,
                  indication:
                    style.geoAttributes
                      .geo_verify_failure_second_button_indication,
                  action:
                    style.geoAttributes.geo_verify_failure_second_button_action,
                  redirect:
                    style.geoAttributes
                      .geo_verify_failure_second_button_redirect,
                  email:
                    style.geoAttributes.geo_verify_failure_second_button_email,
                },
                'Contact Support',
                'email'
              )}
            </div>
          </div>
        )}

        {!verificationResult && (
          <div style={{ marginBottom: '20px' }}>
            <p>Verifying geo position...</p>
          </div>
        )}
        {showBackButton && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
            }}
          >
            <button
              type="button"
              onClick={() => onBack?.()}
              style={{
                fontSize: '18px',
                textDecoration: 'underline',
                color: style?.back_button_text_color || '#07296A',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: style?.back_button_indication || 'Back',
                }}
              />
            </button>
          </div>
        )}
        {showSkipNextScreenButton && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
            }}
          >
            <button
              type="button"
              onClick={onNext}
              style={{
                fontSize: '18px',
                textDecoration: 'underline',
                color: style?.skip_next_screen_button_text_color || '#07296A',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: style?.skip_next_screen_button_indication || 'Skip',
                }}
              />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default GeoVerifyScreen;
